import React, { useState, useEffect, useRef } from "react";
import {
  Input,
  DatePicker,
  Select,
  Table,
  Card,
  Row,
  Col,
  Button,
  Space,
  message,
  Layout,
  Tooltip,
} from "antd";
import {
  SearchOutlined,
  FilterOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { getTypeInfraction } from "../api/typeInfraction/infractionType";
import { getAgentsForFilter } from "../api/constants";
import { statusTextWarning } from "../utils/constants";
import { getArea } from "../api/zone/zoneRequest";
import AreaFilter from "../components/AreaFilter";
import { searchWarningByFilter } from "../api/warmings/warningRequest";
import { useSelector } from "react-redux";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Content } = Layout;

function Warnings() {
  const [infractions, setInfractions] = useState([]);
  const [typeInfractions, setTypeInfractions] = useState([]);
  const [users, setUsers] = useState([]);
  const [area, setArea] = useState([]);

  const [searchMatricule, setSearchMatricule] = useState("");
  const [searchReference, setSearchReference] = useState("");
  const [dateRange, setDateRange] = useState([]);
  const [selectedType, setSelectedType] = useState("");
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState("");
  const [selectedUser, setSelectedUser] = useState("");

  const [selectedZone, setSelectedZone] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedStreet, setSelectedStreet] = useState(null);
  const [selectedLine, setSelectedLine] = useState(null);

  const [loading, setLoading] = useState(false);

  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const user = useSelector((state) => state.user.user);

  const workerPdfRef = useRef(null);
  const workerExcelRef = useRef(null);

  useEffect(() => {
    workerPdfRef.current = new Worker(
      new URL("../workers/pdfWorker.js", import.meta.url)
    );
    workerExcelRef.current = new Worker(
      new URL("../workers/excelWorker.js", import.meta.url)
    );

    workerPdfRef.current.onmessage = (e) => {
      const { status, blobURL, operation, filename, message } = e.data;

      // Handle different status updates
      switch (status) {
        case "starting":
          console.log("PDF generation starting...");
          break;
        case "generating":
          console.log("Creating PDF document...");
          // You could update a more granular status message here if needed
          break;
        case "complete":
          console.log("PDF generation complete");

          if (operation === "download") {
            const link = document.createElement("a");
            link.href = blobURL;
            link.download = filename || "file.pdf";
            link.click();
            URL.revokeObjectURL(blobURL);
          } else if (operation === "open") {
            window.open(blobURL, "_blank");
          }

          break;
        case "error":
          console.error("PDF generation error:", message);
          // message.error(`Erreur lors de la génération du PDF: ${message}`);
          break;
        default:
          console.warn("Unknown status:", status);
          break;
      }
    };

    workerExcelRef.current.onmessage = (e) => {
      const { success, blobURL, filename, error } = e.data;

      if (success) {
        const link = document.createElement("a");
        link.href = blobURL;
        link.download = filename || "file.xlsx";
        link.click();
        URL.revokeObjectURL(blobURL);
      } else {
        message.error(
          `Erreur lors de la génération du fichier Excel: ${error}`
        );
      }
    };

    return () => {
      workerPdfRef.current?.terminate();
      workerExcelRef.current?.terminate();
    };
  }, []);

  useEffect(() => {
    filterInfractions(); // Fetch data initially and whenever filters change
    fetchTypeInfractions();
    fetchUsers();
    fetchZones();
  }, []);

  const filterInfractions = async () => {
    setLoading(true);
    try {
      const filters = {
        reference: searchReference,
        matricule: searchMatricule,
        type_infraction_id: selectedType,
        status: selectedPaymentStatus,
        start_date:
          dateRange && dateRange[0]
            ? dateRange[0].format("YYYY-MM-DD HH:mm:ss")
            : null,
        end_date:
          dateRange && dateRange[1]
            ? dateRange[1].format("YYYY-MM-DD HH:mm:ss")
            : null,
        agent_id: selectedUser,
        zone_id: selectedZone,
        section_id: selectedSection,
        street_id: selectedStreet,
        line_id: selectedLine,
      };
      const data = await searchWarningByFilter(filters);
      setInfractions(data); // Update state with the fetched data
    } catch (error) {
      message.error("Impossible de récupérer les infractions");
      setInfractions([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTypeInfractions = async () => {
    setLoading(true);
    try {
      const data = await getTypeInfraction();
      setTypeInfractions(data); // Update state with the fetched data
    } catch (error) {
      message.error("Impossible de récupérer les types d'infractions");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await getAgentsForFilter();
      setUsers(data); // Update state with the fetched data
    } catch (error) {
      message.error("Échec de la récupération des données");
    } finally {
      setLoading(false);
    }
  };

  const fetchZones = async () => {
    setLoading(true);
    try {
      const data = await getArea();
      setArea(data); // Update state with the fetched data
    } catch (error) {
      message.error("Échec de la récupération des zones");
    } finally {
      setLoading(false);
    }
  };

  const handleExcelClick = async () => {
    setIsExportingExcel(true);
    try {
      const timestamp = new Date().toISOString().replace(/:/g, "-");
      const filename = `Avertissements_${timestamp}.xlsx`;
      const baseUrl = window.location.origin;

      const filters = {
        reference: searchReference,
        matricule: searchMatricule,
        type_infraction_id: selectedType,
        status: selectedPaymentStatus,
        start_date:
          dateRange && dateRange[0]
            ? dateRange[0].format("YYYY-MM-DD HH:mm:ss")
            : null,
        end_date:
          dateRange && dateRange[1]
            ? dateRange[1].format("YYYY-MM-DD HH:mm:ss")
            : null,
        agent_id: selectedUser,
        zone_id: selectedZone,
        section_id: selectedSection,
        street_id: selectedStreet,
        line_id: selectedLine,
      };
      const dataSource = await searchWarningByFilter(filters);

      // Prepare only the necessary data to reduce processing overhead
      const formattedData = dataSource.map((it) => ({
        reference: it.reference,
        user_name: it.user?.full_name,
        type_infraction_name: it.type_infraction?.name,
        matricule: it.matricule,
        brand: it.brand,
        model: it.model,
        datetime_infraction: it.datetime_warning,
        zone_name: it.zone?.name,
        section_name: it.section?.name,
        street_name: it.street?.name,
        line_name: it.line?.name,
        status: statusTextWarning(it.status),
      }));

      const data = [
        ["Avertissements", ``, ``, ``],
        [],
        [],
        user.full_name,
        [],
        [
          [
            {
              text: "Référence",
              type: "string",
              key: "reference",
            },
            {
              text: "Constateur",
              type: "string",
              key: "user_name",
            },
            {
              text: "Type d'infraction",
              type: "string",
              key: "type_infraction_name",
            },
            {
              text: "Matricule",
              type: "string",
              key: "matricule",
            },
            {
              text: "Marque",
              type: "string",
              key: "brand",
            },

            {
              text: "Modèle",
              type: "string",
              key: "model",
            },
            {
              text: "Date et Heure",
              type: "string",
              key: "datetime_infraction",
            },
            {
              text: "Contrat",
              type: "string",
              key: "zone_name",
            },
            {
              text: "Zone",
              type: "string",
              key: "section_name",
            },
            {
              text: "Rue",
              type: "string",
              key: "street_name",
            },
            {
              text: "Line",
              type: "string",
              key: "line_name",
            },
            {
              text: "Statut",
              type: "string",
              key: "status",
            },
          ],
        ],
        [formattedData],
        baseUrl,
      ];

      // Post message to worker
      workerExcelRef.current.postMessage({ data, filename });
    } catch (error) {
      message.error("Erreur lors de la génération du fichier Excel");
    } finally {
      setIsExportingExcel(false);
    }
  };

  const columns = [
    {
      title: "Référence",
      dataIndex: "reference",
      key: "reference",
    },
    // {
    //   title: "ID",
    //   dataIndex: "id",
    //   key: "id",
    // },
    {
      title: "Constateur",
      dataIndex: "agent_id",
      key: "agent_id",
      render: (_, record) => record.user.full_name,
    },
    {
      title: "Type d'infraction",
      dataIndex: "type_infraction_id",
      key: "type_infraction_id",
      ellipsis: true,
      render: (_, record) => record.type_infraction.name,
    },

    { title: "Matricule", dataIndex: "matricule", key: "matricule" },
    { title: "Marque", dataIndex: "brand", key: "brand" },
    { title: "Modèle", dataIndex: "model", key: "model" },
    {
      title: "Date et Heure",
      dataIndex: "datetime_warning",
      key: "datetime_warning",
    },
    {
      title: "Localisation",
      dataIndex: "zone",
      key: "zone",
      ellipsis: true,
      render: (_, record) =>
        `${record?.zone?.name} > ${record?.section?.name} > ${record?.street?.name} > ${record?.line?.name}`,
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      render: (status) => statusTextWarning(status),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button
          type="link"
          onClick={() => window.open(`/warning/${record.id}`, "_blank")}
        >
          Détails
        </Button>
      ),
    },
  ];

  return (
    <Layout className="min-h-screen ">
      <Content className="py-6 sm:px-6 lg:px-8">
        <Card className="mb-6 shadow-sm">
          <Space direction="vertical" size="middle" className="w-full">
            <Row gutter={16} align="middle" className="gap-y-2">
              <Col xs={24} sm={12} md={4}>
                <Input
                  placeholder="Rechercher par Référence"
                  prefix={<SearchOutlined />}
                  value={searchReference}
                  onChange={(e) => setSearchReference(e.target.value)}
                />
              </Col>
              <Col xs={24} sm={12} md={4}>
                <Input
                  placeholder="Rechercher par Matricule"
                  prefix={<SearchOutlined />}
                  value={searchMatricule}
                  onChange={(e) => setSearchMatricule(e.target.value)}
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <RangePicker
                  onChange={(dates) => setDateRange(dates)}
                  className="w-full"
                  showTime={{ format: "HH:mm" }}
                />
              </Col>
              <Col xs={24} sm={12} md={4}>
                <Select
                  placeholder="Filtrer par Type"
                  style={{ width: "100%" }}
                  onChange={(value) => setSelectedType(value)}
                  allowClear
                >
                  {/* Populate this with options as needed */}
                  {typeInfractions.map((type) => (
                    <Option key={type.id} value={type.id}>
                      {type.name}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={4}>
                <Select
                  placeholder="Filtrer par Statut"
                  style={{ width: "100%" }}
                  onChange={(value) => setSelectedPaymentStatus(value)}
                  allowClear
                >
                  <Option value="finalized">Infraction confirmée</Option>
                  <Option value="pending">En attente</Option>
                  <Option value="canceled">Annulé</Option>
                </Select>
              </Col>
              <Col xs={24} sm={12} md={4}>
                <Select
                  showSearch
                  placeholder="Filtrer par Constateur"
                  style={{ width: "100%" }}
                  onChange={(value) => setSelectedUser(value)}
                  allowClear
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    String(option?.children).toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {users.map((user) => (
                    <Option key={user.id_agent} value={user.id_agent}>
                      {user.full_name}
                    </Option>
                  ))}
                </Select>
              </Col>

              <AreaFilter
                data={area}
                selectedZone={selectedZone}
                selectedSection={selectedSection}
                selectedStreet={selectedStreet}
                selectedLine={selectedLine}
                setSelectedZone={setSelectedZone}
                setSelectedSection={setSelectedSection}
                setSelectedStreet={setSelectedStreet}
                setSelectedLine={setSelectedLine}
              />
            </Row>

            <Row gutter={16} align="middle">
              <Col xs={24} sm={12} md={4}>
                <Button
                  type="primary"
                  icon={<FilterOutlined />}
                  onClick={filterInfractions}
                  loading={loading}
                  className="w-full"
                >
                  Rechercher
                </Button>
              </Col>
            </Row>
          </Space>
        </Card>

        <Card className="shadow-sm">
          <div style={{ marginBottom: 16 }} className="flex justify-end">
            <Button
              icon={<FileTextOutlined />}
              onClick={async () => await handleExcelClick()}
              className="hover:border-green-700 hover:text-green-500 mr-4"
              disabled={isExportingExcel}
            >
              Exporter Excel
            </Button>
          </div>
          <Table
            dataSource={infractions}
            columns={columns}
            rowKey={(record) => record.id}
            loading={loading}
            pagination={{
              showSizeChanger: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} sur ${total} avertissements`,
            }}
          />
        </Card>
      </Content>
    </Layout>
  );
}

export default Warnings;
